// An agent's own column widths, column order, sort and removed columns for a list, kept in this browser per
// tenant and per view (a ticket list view, or a My Work tab). Personal settings always win over what a saved
// view was saved with.

export type ColumnWidths = Record<string, number>;

export enum ListPreference {
  TicketListColumnWidths = 'ticketListColumnWidths',
  TicketListColumnOrder = 'ticketListColumnOrder',
  TicketListSortOrder = 'ticketListSortOrder',
  TicketListHiddenColumns = 'ticketListHiddenColumns',
  MyWorkColumnWidths = 'myWorkColumnWidths',
  MyWorkColumnOrder = 'myWorkColumnOrder',
  MyWorkHiddenColumns = 'myWorkHiddenColumns',
  MyGroupWorkColumnWidths = 'myGroupWorkColumnWidths',
  MyGroupWorkColumnOrder = 'myGroupWorkColumnOrder',
  MyGroupWorkHiddenColumns = 'myGroupWorkHiddenColumns',
  UserTicketsColumnWidths = 'userTicketsColumnWidths',
  UserTicketsColumnOrder = 'userTicketsColumnOrder',
  UserTicketsSortOrder = 'userTicketsSortOrder',
  UserTicketsHiddenColumns = 'userTicketsHiddenColumns',
  UsersSortOrder = 'usersSortOrder',
  KnowledgeSortOrder = 'knowledgeSortOrder',
  LicenseDistributionSortOrder = 'licenseDistributionSortOrder',
}

interface SizedColumn {
  key: string;
  minWidth: number;
  maxWidth?: number;
  calculatedWidth?: number;
}

// How narrow an agent can drag a column. The columns' own minWidth is their default width, so without
// this a column could be widened but never made narrower than it started.
export const COLUMN_DRAG_FLOOR = 50;

export const listPreferenceKey = (preference: ListPreference, tenantId: string | null, scope: string | number) =>
  `${preference}:${tenantId ?? ''}:${scope}`;

// A saved view is named in the url as ?id=; a system view (All Active, My Tickets...) only in
// storage. The url wins so a shared link to a view never picks up the last view's settings.
export const ticketListViewScope = (search: string, customViewId?: number, defaultViewId?: number) => {
  const urlViewId = new URLSearchParams(search).get('id');
  if (urlViewId) return `custom-${urlViewId}`;
  if (customViewId) return `custom-${customViewId}`;
  if (defaultViewId != null) return `system-${defaultViewId}`;
  return 'system';
};

export const readListPreference = <T>(storageKey: string): T | null => {
  try {
    const value = localStorage.getItem(storageKey);
    return value ? JSON.parse(value) : null;
  } catch {
    return null;
  }
};

// Announced after every write, so the toolbar's Reset view can light up the moment a column is dragged
// or sorted, and a list can rebuild when its settings are cleared.
export const LIST_PREFERENCES_CHANGED = 'tikit:list-preferences-changed';

export interface ListPreferencesChangedDetail {
  storageKey: string;
  cleared: boolean;
}

export const writeListPreference = (storageKey: string, value: unknown) => {
  try {
    if (value == null) localStorage.removeItem(storageKey);
    else localStorage.setItem(storageKey, JSON.stringify(value));
  } catch {
    // Storage full or blocked: the setting lasts until the list is rebuilt.
  }
  if (typeof window !== 'undefined' && typeof window.dispatchEvent === 'function' && typeof CustomEvent === 'function') {
    const detail: ListPreferencesChangedDetail = { storageKey, cleared: value == null };
    window.dispatchEvent(new CustomEvent(LIST_PREFERENCES_CHANGED, { detail }));
  }
};

export const readColumnWidths = (storageKey: string): ColumnWidths => {
  const widths = readListPreference<ColumnWidths>(storageKey);
  return widths && typeof widths === 'object' && !Array.isArray(widths) ? widths : {};
};

export const saveColumnWidth = (storageKey: string, columnKey: string, width: number) => {
  if (!Number.isFinite(width)) return;
  writeListPreference(storageKey, { ...readColumnWidths(storageKey), [columnKey]: Math.round(width) });
};

export const readColumnOrder = (storageKey: string): string[] => {
  const order = readListPreference<unknown>(storageKey);
  return Array.isArray(order) ? order.filter((key): key is string => typeof key === 'string') : [];
};

// Columns the agent has placed come first, in their saved order. Anything not in the saved order - a column
// added since - is not lost: a built-in column goes straight after the nearest column that comes before it by
// default and is in the saved order (Tags after Assignee, wherever the agent has put Assignee - Shaun, 2026-10-13);
// a custom property, or a column with no such neighbour, follows at the end in its own order.
export const applyColumnOrder = <T extends { key: string }>(columns: T[], order: string[]): T[] => {
  if (order.length === 0) return columns;
  const byKey = new Map(columns.map(column => [column.key, column] as [string, T]));
  const placed = order.flatMap(key => {
    const column = byKey.get(key);
    return column ? [column] : [];
  });
  const placedKeys = new Set(placed.map(column => column.key));
  const result = [...placed];
  const added = new Set<string>();
  columns.forEach((column, index) => {
    if (placedKeys.has(column.key)) return;
    let at = result.length;
    if (Number(column.key) < CUSTOM_PROPERTY_COLUMN_BASE) {
      const before = columns.slice(0, index).reverse().find(other => placedKeys.has(other.key));
      if (before) {
        at = result.indexOf(before) + 1;
        while (at < result.length && added.has(result[at].key)) at++;
      }
    }
    result.splice(at, 0, column);
    added.add(column.key);
  });
  return result;
};

// Columns an agent has removed from a list with the X on its header: hidden for them, on this view, in this browser,
// until Reset View. Only the keys they removed are kept, so a column added to the view later still appears.
export const readHiddenColumns = (storageKey: string): string[] => readColumnOrder(storageKey);

export const hideColumn = (storageKey: string, columnKey: string) => {
  const hidden = readHiddenColumns(storageKey);
  if (!hidden.includes(columnKey)) writeListPreference(storageKey, [...hidden, columnKey]);
};

// Id and Request stay on every list - the Configure Columns dialog will not save a view without one of them.
const LOCKED_COLUMNS = ['1', '2'];

/** Whether a column shows the X: not Id or Request, and never the last column on the list. */
export const canRemoveColumn = (columnKey: string, shownColumnCount: number) =>
  !LOCKED_COLUMNS.includes(String(columnKey)) && shownColumnCount > 1;

// Never hides every column: a list that would be left with none shows them all.
export const applyHiddenColumns = <T extends { key: string }>(columns: T[], hidden: string[]): T[] => {
  if (hidden.length === 0) return columns;
  const shown = columns.filter(column => !hidden.includes(column.key));
  return shown.length > 0 ? shown : columns;
};

/** Custom property columns are numbered from here (1000 + the property's id); built-in columns sit below. */
const CUSTOM_PROPERTY_COLUMN_BASE = 1000;

// Fluent reports a header drop as positions in the columns it was given, and leaves the moving to us.
export const moveColumn = <T>(columns: T[], draggedIndex: number, targetIndex: number): T[] => {
  if (draggedIndex === targetIndex || draggedIndex < 0 || draggedIndex >= columns.length) return columns;
  const moved = columns.slice();
  const [column] = moved.splice(draggedIndex, 1);
  moved.splice(Math.max(0, Math.min(targetIndex, moved.length)), 0, column);
  return moved;
};

// Fluent's fixed-columns layout takes a column's starting width from calculatedWidth, and stops a drag at
// minWidth. So every column starts at its saved width or, failing that, the width it always had (maxWidth,
// else minWidth, as Fluent itself would pick) - and its minWidth drops to the drag floor, so it can be
// made narrower than that default. A saved width below the floor is raised to it.
export const applyColumnWidths = <T extends SizedColumn>(columns: T[], widths: ColumnWidths, floor = COLUMN_DRAG_FLOOR): T[] =>
  columns.map(column => {
    const saved = widths[column.key];
    const minWidth = Math.min(column.minWidth, floor);
    const calculatedWidth = typeof saved === 'number' && Number.isFinite(saved)
      ? Math.max(saved, minWidth)
      : column.maxWidth || column.minWidth;
    return { ...column, minWidth, calculatedWidth };
  });
